import React, { useState, useEffect } from "react";
import { Grid, Box, FormControlLabel, Radio } from "@mui/material";
import { Formik, Form } from "formik";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { GridCellParams, GridColDef } from "@mui/x-data-grid";
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from "@mui/icons-material/Search";
import DateRange from "../../components/DateRange";
import Loader from "../../components/Loader";
import TextField from "../../components/TextField"
import Dropdown from "../../components/Dropdown";
import Button from "../../components/Button";
import GridTable from "../../components/GridTable";
import ActionMenu from "../../components/ActionMenu";
import { getErrorMessage } from "../../utils/error";
import { getAllCompanies } from "../company/api";
import { getAllCrops } from "../crop/api";
import { CompanyData } from "../company/interface";
import { CropData } from "../crop/interface";
import { getCalibrationData } from "./api";
import { CalibrationRequestParams, CombinedCalibrationResponse, CalibrationScanData, CalibrationScanLogs, CalibrationData } from "./interface";
import { successMessage, errorMessage } from "../../components/Toaster";


const Calibration: React.FC = () => {
    type ScanDataType = 'grain' | 'mapping' | 'points' | 'performance' | 'images';
    const [loading, setLoading] = useState(false);
    const [companies, setCompanies] = useState<CompanyData[]>([]);
    const [crops, setCrops] = useState<CropData[]>([]);
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
        dayjs(),
        dayjs(),
    ]);
    dayjs.extend(utc);
    dayjs.extend(timezone);
    const [searchQuery, setSearchQuery] = useState("");
    const [combinedCalibrationData, setCombinedCalibrationData] = useState<CombinedCalibrationResponse | null>(null);
    const [calibrationScanData, setCalibrationScanData] = useState<any | []>([]);
    const [selectedSection, setSelectedSection] = useState("calibration_scan_data");
    const [selectedScanDataType, setSelectedScanDataType] = useState<ScanDataType>('grain');
    const [selectedRows, setSelectedRows] = useState<[]>([]);
    const [selectedLogRows, setSelectedLogRows] = useState<CalibrationScanLogs[]>([]);

    const grainColumns = [
        { field: 'scan_id', headerName: 'Scan ID', headerClassName: "table-header", width: 150 },
        { field: 'sub_scan_id', headerName: 'Sub Scan ID', headerClassName: "table-header", width: 150 },
        { field: 'company_name', headerName: 'Company Name', headerClassName: "table-header", width: 150 },
        { field: 'user_name', headerName: 'User Name', headerClassName: "table-header", width: 150 },
        { field: 'count', headerName: 'Count', headerClassName: "table-header", width: 150 },
        { field: 'comment', headerName: 'Comment', headerClassName: "table-header", width: 150 },
        { field: 'calibration_id', headerName: 'Calibration ID', headerClassName: "table-header", width: 150 },
        { field: 'machine_instance_id', headerName: 'Machine Instance ID', headerClassName: "table-header", width: 170 },
        { field: 'machine_code', headerName: 'Machine Code', headerClassName: "table-header", width: 150 },
        { field: 'crop_name', headerName: 'Crop Name', headerClassName: "table-header", width: 150 },
        { field: 'timestamp', headerName: 'Date', headerClassName: "table-header", width: 170 },
    ];

    const mappingColumns = [
        { field: 'scan_id', headerName: 'Scan ID', headerClassName: "table-header", width: 150 },
        { field: 'sub_scan_id', headerName: 'Sub Scan ID', headerClassName: "table-header", width: 150 },
        { field: 'company_name', headerName: 'Company Name', headerClassName: "table-header", width: 150 },
        { field: 'user_name', headerName: 'User Name', headerClassName: "table-header", width: 150 },
        {
            field: 'correct',
            headerName: 'Correct',
            headerClassName: 'table-header',
            width: 150,
            renderCell: (params: GridCellParams) => (params.row.correct === true ? 'Yes' : 'No'),
        },
        { field: 'comment', headerName: 'Comment', headerClassName: "table-header", width: 150 },
        { field: 'calibration_id', headerName: 'Calibration ID', headerClassName: "table-header", width: 150 },
        { field: 'machine_instance_id', headerName: 'Machine Instance ID', headerClassName: "table-header", width: 170 },
        { field: 'machine_code', headerName: 'Machine Code', headerClassName: "table-header", width: 150 },
        { field: 'crop_name', headerName: 'Crop Name', headerClassName: "table-header", width: 150 },
        { field: 'timestamp', headerName: 'Date', headerClassName: "table-header", width: 170 },
    ];

    const qrColumns = [
        { field: 'scan_id', headerName: 'Scan ID', headerClassName: 'table-header', width: 150 },
        { field: 'sub_scan_id', headerName: 'Sub Scan ID', headerClassName: 'table-header', width: 150 },
        { field: 'company_name', headerName: 'Company Name', headerClassName: 'table-header', width: 150 },
        { field: 'user_name', headerName: 'User Name', headerClassName: 'table-header', width: 150 },
        { field: 'calibration_id', headerName: 'Calibration ID', headerClassName: "table-header", width: 150 },
        { field: 'machine_instance_id', headerName: 'Machine Instance ID', headerClassName: "table-header", width: 170 },
        { field: 'machine_code', headerName: 'Machine Code', headerClassName: "table-header", width: 150 },
        { field: 'crop_name', headerName: 'Crop Name', headerClassName: "table-header", width: 150 },
        { field: 'qr_points', headerName: 'QR Points', headerClassName: 'table-header', width: 260 },
        { field: 'top_points', headerName: 'Top Points', headerClassName: 'table-header', width: 260 },
        { field: 'bottom_points', headerName: 'Bottom Points', headerClassName: 'table-header', width: 260 },
        { field: 'top_coin_radius', headerName: 'Top Coin Radius', headerClassName: 'table-header', width: 150 },
        { field: 'bottom_coin_radius', headerName: 'Bottom Coin Radius', headerClassName: 'table-header', width: 170 },
        { field: 'top_coin_x_center', headerName: 'Top Coin X Center', headerClassName: 'table-header', width: 170 },
        { field: 'bottom_coin_x_center', headerName: 'Bottom Coin X Center', headerClassName: 'table-header', width: 190 },
        { field: 'top_coin_y_center', headerName: 'Top Coin Y Center', headerClassName: 'table-header', width: 170 },
        { field: 'bottom_coin_y_center', headerName: 'Bottom Coin Y Center', headerClassName: 'table-header', width: 190 },
        { field: 'resized_bottom_coin_radius', headerName: 'Resized Bottom Radius', headerClassName: 'table-header', width: 190 },
        { field: 'resized_bottom_coin_x_center', headerName: 'Resized Bottom X Center', headerClassName: 'table-header', width: 200 },
        { field: 'resized_top_coin_y_center', headerName: 'Resized Top Y Center', headerClassName: 'table-header', width: 190 },
        { field: 'timestamp', headerName: 'Date', headerClassName: "table-header", width: 170 },
    ];

    const performanceColumns = [
        { field: 'scan_id', headerName: 'Scan ID', headerClassName: 'table-header', width: 150 },
        { field: 'company_name', headerName: 'Company Name', headerClassName: 'table-header', width: 150 },
        { field: 'user_name', headerName: 'User Name', headerClassName: 'table-header', width: 150 },
        { field: 'calibration_id', headerName: 'Calibration ID', headerClassName: "table-header", width: 150 },
        { field: 'machine_instance_id', headerName: 'Machine Instance ID', headerClassName: "table-header", width: 170 },
        { field: 'machine_code', headerName: 'Machine Code', headerClassName: "table-header", width: 150 },
        { field: 'crop_name', headerName: 'Crop Name', headerClassName: "table-header", width: 140 },
        { field: 'time to startScan', headerName: 'Time to Start Scan', headerClassName: 'table-header', width: 150 },
        { field: 'time to start capturing image', headerName: 'Time to Start Capturing Image', headerClassName: 'table-header', width: 240 },
        { field: 'time to click capture image button', headerName: 'Time to Click Capture Image Button', headerClassName: 'table-header', width: 270 },
        { field: 'time to capture image', headerName: 'Time to Capture Image', headerClassName: 'table-header', width: 190 },
        { field: 'Time to save file_top_captured__0.jpg image', headerName: 'Time to Save file_top_captured__0.jpg Image', headerClassName: 'table-header', width: 340 },
        { field: 'time to click capture image button_2', headerName: 'Time to Click Capture Image Button 2', headerClassName: 'table-header', width: 290 },
        { field: 'time to capture image_2', headerName: 'Time to Capture Image 2', headerClassName: 'table-header', width: 200 },
        { field: 'Time to save file_bottom_captured__0.jpg image', headerName: 'Time to Save file_bottom_captured__0.jpg Image', headerClassName: 'table-header', width: 370 },
        { field: 'time to compress images', headerName: 'Time to Compress Images', headerClassName: 'table-header', width: 220 },
        { field: 'time to for API call before inference', headerName: 'Time to API Call Before Inference', headerClassName: 'table-header', width: 270 },
        { field: 'timestamp', headerName: 'Date', headerClassName: "table-header", width: 170 },
    ];

    const imagesColumns = [
        { field: 'scan_id', headerName: 'Scan ID', headerClassName: 'table-header', width: 150 },
        { field: 'sub_scan_id', headerName: 'Sub Scan ID', headerClassName: 'table-header', width: 150 },
        { field: 'company_name', headerName: 'Company Name', headerClassName: 'table-header', width: 150 },
        { field: 'user_name', headerName: 'User Name', headerClassName: 'table-header', width: 150 },
        { field: 'calibration_id', headerName: 'Calibration ID', headerClassName: 'table-header', width: 150 },
        { field: 'machine_instance_id', headerName: 'Machine Instance ID', headerClassName: 'table-header', width: 170 },
        { field: 'machine_code', headerName: 'Machine Code', headerClassName: 'table-header', width: 150 },
        { field: 'crop_name', headerName: 'Crop Name', headerClassName: 'table-header', width: 150 },
        { field: 'timestamp', headerName: 'Date', headerClassName: "table-header", width: 170 },
        {
            field: "full_raw",
            headerName: "Full Raw",
            width: 150,
            headerClassName: "table-header",
            sortable: false,
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            tooltip: "Top",
                            label: "Top",
                            useButton: true,
                            onClick: (params) => {
                                const { scan_id, sub_scan_id, full_raw_top } = params.row;
                                downloadImage(full_raw_top, `${scan_id}_${sub_scan_id}_full_raw_top`);
                            },
                        },
                        {
                            tooltip: "Bottom",
                            label: "Bottom",
                            useButton: true,
                            onClick: (params: GridCellParams) => {
                                const { scan_id, sub_scan_id, full_raw_bottom } = params.row;
                                downloadImage(full_raw_bottom, `${scan_id}_${sub_scan_id}_full_raw_bottom`);
                            },
                        },
                    ]}
                />
            ),
        },
        {
            field: "cropped",
            headerName: "Cropped",
            width: 150,
            headerClassName: "table-header",
            sortable: false,
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            tooltip: "Top",
                            label: "Top",
                            useButton: true,
                            onClick: (params: GridCellParams) => {
                                const { scan_id, sub_scan_id, cropped_top } = params.row;
                                downloadImage(cropped_top, `${scan_id}_${sub_scan_id}_cropped_top`);
                            },
                        },
                        {
                            tooltip: "Bottom",
                            label: "Bottom",
                            useButton: true,
                            onClick: (params: GridCellParams) => {
                                const { scan_id, sub_scan_id, cropped_bottom } = params.row;
                                downloadImage(cropped_bottom, `${scan_id}_${sub_scan_id}_cropped_bottom`);
                            },
                        },
                    ]}
                />
            ),
        },
        {
            field: "processed_segmented",
            headerName: "Processed Segmented",
            width: 180,
            headerClassName: "table-header",
            sortable: false,
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <DownloadIcon color="primary" />,
                            tooltip: "Processed Segmented",
                            label: "download",
                            onClick: () => {
                                const { scan_id, sub_scan_id, processed_segmented } = params.row;
                                downloadImage(processed_segmented, `${scan_id}_${sub_scan_id}_processed_segmented`);
                            },
                        },
                    ]}
                />
            ),
        },
        {
            field: "processed_classes",
            headerName: "Processed Classes",
            width: 180,
            headerClassName: "table-header",
            sortable: false,
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <DownloadIcon color="primary" />,
                            tooltip: "Classes",
                            label: "Classes",
                            onClick: () => {
                                const { scan_id, sub_scan_id, processed_classes } = params.row;
                                downloadImage(processed_classes, `${scan_id}_${sub_scan_id}_processed_classes`);
                            },
                        },
                    ]}
                />
            ),
        },

    ];

    const calibrationScanColumns: Record<ScanDataType, GridColDef[]> = {
        grain: grainColumns,
        mapping: mappingColumns,
        points: qrColumns,
        performance: performanceColumns,
        images: imagesColumns
    };

    const calibrationScanDataOptions: { label: string; value: ScanDataType }[] = [
        { label: 'Grain Data', value: 'grain' },
        { label: 'Mapping Data', value: 'mapping' },
        { label: 'QR Points', value: 'points' },
        { label: 'Performance', value: 'performance' },
        { label: 'Images', value: 'images' },
    ];

    const calinbrationScanLogColumns = [
        { field: 'scan_id', headerName: 'Scan ID', headerClassName: "table-header", flex: 0.8 },
        { field: 'company_name', headerName: 'Company Name', headerClassName: "table-header", flex: 1 },
        { field: 'user_name', headerName: 'User Name', headerClassName: "table-header", flex: 1 },
        { field: 'calibration_id', headerName: 'Calibration ID', headerClassName: "table-header", flex: 1 },
        { field: 'machine_instance_id', headerName: 'Machine Instance ID', headerClassName: "table-header", flex: 1.1 },
        { field: 'machine_code', headerName: 'Machine Code', headerClassName: "table-header", flex: 1 },
        { field: 'crop_name', headerName: 'Crop Name', headerClassName: "table-header", flex: 0.8 },
        {
            field: "timestamp",
            headerName: "Date",
            flex: 1,
            renderCell: (params: any) =>
                params.value
                    ? dayjs.utc(params.value).tz("Asia/Kolkata").format("DD/MM/YYYY hh:mm A")
                    : "",
        },
        {
            field: 'action',
            headerName: 'Log Files',
            flex: 1.5,
            headerClassName: "table-header",
            sortable: false,
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            tooltip: "Download App Log",
                            label: "App",
                            useButton: true,
                            onClick: () => {
                                handleDownloadAppLog(params.row);
                            },
                        },
                        {
                            useButton: true,
                            tooltip: "Download Server Log",
                            label: "Server",
                            onClick: () => {
                                handleDownloadServerLog(params.row);
                            },
                        },
                    ]}
                />
            ),

        },
    ];

    const calinbrationDataColumns = [
        { field: 'scan_id', headerName: 'Scan ID', headerClassName: "table-header", flex: 0.8 },
        { field: 'company_name', headerName: 'Company Name', headerClassName: "table-header", flex: 1 },
        { field: 'user_name', headerName: 'User Name', headerClassName: "table-header", flex: 1 },
        { field: 'calibration_id', headerName: 'Calibration ID', headerClassName: "table-header", flex: 1 },
        { field: 'machine_instance_id', headerName: 'Machine Instance ID', headerClassName: "table-header", flex: 1.1 },
        { field: 'machine_code', headerName: 'Machine Code', headerClassName: "table-header", flex: 1 },
        { field: 'crop_name', headerName: 'Crop Name', headerClassName: "table-header", flex: 0.8 },
        {
            field: "timestamp",
            headerName: "Date",
            flex: 1,
            renderCell: (params: any) =>
                params.value
                    ? dayjs.utc(params.value).tz("Asia/Kolkata").format("DD/MM/YYYY hh:mm A")
                    : "",
        },
        {
            field: 'Calibration_data',
            headerName: 'Calibration Data',
            flex: 1,
            headerClassName: "table-header",
            sortable: false,
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <DownloadIcon color="primary" />,
                            tooltip: "Download",
                            onClick: () => {
                                handleDownloadCalibrationDataJson(params.row);
                            },
                        },
                    ]}
                />
            ),
        },
        {
            field: 'action',
            headerName: 'Images',
            flex: 1,
            headerClassName: "table-header",
            sortable: false,
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <DownloadIcon color="primary" />,
                            tooltip: "Download",
                            onClick: () => {
                                handleDownloadCalibrationDataImages(params.row);
                            },
                        },
                    ]}
                />
            ),

        },
    ];

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                setLoading(true);
                const [companiesRes, cropsRes] = await Promise.all([
                    getAllCompanies(),
                    getAllCrops(),
                ]);
                setCompanies(companiesRes.data);
                setCrops(cropsRes.data ?? []);
                const params = {
                    start_date: `${dayjs(dateRange[0]).format("YYYY-MM-DD")}T00:00:00Z`,
                    end_date: `${dayjs(dateRange[1]).format("YYYY-MM-DD")}T23:59:59Z`,
                };
                const calibrationData = await getCalibrationData(params);
                setCombinedCalibrationData(calibrationData.data)
            } catch (error) {
                errorMessage(getErrorMessage(error) || "Error fetching Data");
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, []);

    useEffect(() => {
        if (!combinedCalibrationData) return;

        const transformCVData = () => {
            switch (selectedScanDataType) {
                case 'grain':
                    return transformGrainData(combinedCalibrationData.calibration_scan_data);
                case 'mapping':
                    return transformMappingData(combinedCalibrationData.calibration_scan_data);
                case 'points':
                    return transformQRData(combinedCalibrationData.calibration_scan_data);
                case 'performance':
                    return transformPerformanceData(combinedCalibrationData.calibration_scan_data);
                case 'images':
                    return transformImagesData(combinedCalibrationData.calibration_scan_data);
                default:
                    return [];
            }
        };

        const data = transformCVData();
        setCalibrationScanData(data);
    }, [selectedScanDataType, combinedCalibrationData]);

    const downloadImage = async (url: string, filename: string) => {
        if (!url) {
            errorMessage("No File to Download");
            return;
        }
        try {
            setLoading(true)
            const response = await fetch(url, { mode: 'cors' });
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = filename || "image.jpg";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Download failed:", error);
        }
        setLoading(false)
    };

    const transformGrainData = (data: CalibrationScanData[]) => {
        return data.flatMap(scan => {
            const grainData = scan?.regular_calibration?.grain_result?.grain_data || [];
            return grainData.map(sub => ({
                id: `${scan.scan_id}_${sub.sub_scan}`,
                scan_id: scan.scan_id,
                sub_scan_id: sub.sub_scan,
                company_name: scan.company_name,
                user_name: scan.user_name,
                calibration_id: scan.calibration_id,
                machine_instance_id: scan.machine_instance_id,
                machine_code: scan.machine_code,
                crop_name: scan.crop_name,
                timestamp: scan.timestamp ? dayjs.utc(scan.timestamp).tz("Asia/Kolkata").format("DD/MM/YYYY hh:mm A") : null,
                ...sub,
            }));
        });
    };

    const transformMappingData = (data: CalibrationScanData[]) => {
        return data.flatMap(scan => {
            const mappingData = scan?.regular_calibration?.mapping_result?.mapping_data || [];
            return mappingData.map(sub => ({
                id: `${scan.scan_id}_${sub.sub_scan}`,
                scan_id: scan.scan_id,
                sub_scan_id: sub.sub_scan,
                company_name: scan.company_name,
                user_name: scan.user_name,
                calibration_id: scan.calibration_id,
                machine_instance_id: scan.machine_instance_id,
                machine_code: scan.machine_code,
                crop_name: scan.crop_name,
                timestamp: scan.timestamp ? dayjs.utc(scan.timestamp).tz("Asia/Kolkata").format("DD/MM/YYYY hh:mm A") : null,
                ...sub,
            }));
        });
    };

    const transformQRData = (data: CalibrationScanData[]) => {
        return data.flatMap(scan => {
            const points = scan?.regular_calibration?.points?.points_data || [];
            return points.map(sub => ({
                id: `${scan.scan_id}_${sub.sub_scan}`,
                scan_id: scan.scan_id,
                sub_scan_id: sub.sub_scan,
                company_name: scan.company_name,
                user_name: scan.user_name,
                calibration_id: scan.calibration_id,
                machine_instance_id: scan.machine_instance_id,
                machine_code: scan.machine_code,
                crop_name: scan.crop_name,
                timestamp: scan.timestamp ? dayjs.utc(scan.timestamp).tz("Asia/Kolkata").format("DD/MM/YYYY hh:mm A") : null,
                ...sub,
            }));
        });
    };

    const transformPerformanceData = (data: CalibrationScanData[]) => {
        return data.map(scan => ({
            ...scan.performace?.performance_data,
            id: scan.scan_id,
            scan_id: scan.scan_id,
            company_name: scan.company_name,
            user_name: scan.user_name,
            calibration_id: scan.calibration_id,
            machine_instance_id: scan.machine_instance_id,
            machine_code: scan.machine_code,
            crop_name: scan.crop_name,
            timestamp: scan.timestamp ? dayjs.utc(scan.timestamp).tz("Asia/Kolkata").format("DD/MM/YYYY hh:mm A") : null,
        }));
    };

    const transformImagesData = (data: CalibrationScanData[]) => {
        return data.flatMap(scan => {
            const imageData = scan?.calibration_scan_images?.calibation_scan_images_data || [];
            const grouped: Record<string, any> = {};

            imageData.forEach(img => {
                const uniqueId = `${scan.scan_id}_${img.sub_scan}`;
                if (!grouped[uniqueId]) {
                    grouped[uniqueId] = {
                        id: uniqueId,
                        sub_scan_id: img.sub_scan,
                        scan_id: scan.scan_id,
                        company_name: scan.company_name,
                        user_name: scan.user_name,
                        calibration_id: scan.calibration_id,
                        machine_instance_id: scan.machine_instance_id,
                        machine_code: scan.machine_code,
                        crop_name: scan.crop_name,
                        timestamp: scan.timestamp ? dayjs.utc(scan.timestamp).tz("Asia/Kolkata").format("DD/MM/YYYY hh:mm A") : null,
                    };
                }

                const key = img.file_name.replace(/\.[^/.]+$/, "");
                grouped[uniqueId][key] = img.file_url;
            });

            return Object.values(grouped);
        });
    };

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedSection(event.target.value);
    };

    const handleDownloadAppLog = (row: CalibrationScanLogs) => {
        if (!row.app_file_path) {
            errorMessage("No File to Download");
            return;
        }
        const machineId = row.machine_instance_id;
        const scanId = row.scan_id;
        const fileName = scanId
            ? `machine_${machineId}_scan_${scanId}_app_log`
            : `machine_${machineId}_app_log`;

        downloadImage(row.app_file_path ?? '', fileName);
    };

    const handleDownloadServerLog = (row: CalibrationScanLogs) => {
        if (!row.server_file_path) {
            errorMessage("No File to Download");
            return;
        }
        const machineId = row.machine_instance_id;
        const scanId = row.scan_id;
        const fileName = scanId
            ? `machine_${machineId}_scan_${scanId}_server_log`
            : `machine_${machineId}_server_log`;

        downloadImage(row.server_file_path ?? '', fileName);
    };

    const handleDownloadCalibrationDataJson = (row: CalibrationData) => {
        setLoading(true);
        const { calibration_id, calibration } = row;
        if (!calibration) {
            errorMessage("No Calibration Data to Download");
            return;
        }

        const fileName = `calibration_${calibration_id}.json`;
        const blob = new Blob([JSON.stringify(calibration, null, 2)], { type: "application/json" });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        setLoading(false);
    };

    const handleSubmit = async (values: any) => {
        try {
            setLoading(true);
            const params: CalibrationRequestParams = {
                company_id: values.companyId,
                crop_id: values.cropId,
                start_date: `${dateRange[0]?.format("YYYY-MM-DD")}T00:00:00Z`,
                end_date: `${dateRange[1]?.format("YYYY-MM-DD")}T23:59:59Z`
            };

            const calibrationData = await getCalibrationData(params);
            setCombinedCalibrationData(calibrationData.data ?? [])
            successMessage("Calibration data fetched successfully");
        } catch (error) {
            errorMessage(getErrorMessage(error) || "Error fetching calibration Data");
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadZipForCalibrationScanLogs = async (rows: CalibrationScanLogs[]) => {
        setLoading(true);
        const zip = new JSZip();
        for (const row of rows) {
            const machineId = row.machine_instance_id;
            const scanId = row.scan_id;
            const folderName = scanId
                ? `machine_${machineId}_scan_${scanId}`
                : `machine_${machineId}`;
            const folder = zip.folder(folderName);
            const filesToDownload = [
                { url: row.app_file_path, name: 'app_file_path' },
                { url: row.server_file_path, name: 'server_file_path' },
            ];
            for (const file of filesToDownload) {
                if (file.url) {
                    try {
                        const response = await fetch(file.url);
                        const blob = await response.blob();
                        const extension = file.url.split('.').pop()?.split(/[#?]/)[0] || 'jpg';
                        folder?.file(`${file.name}.${extension}`, blob);
                    } catch (err) {
                        console.error(`Failed to fetch ${file.url}`, err);
                    }
                }
            }
        }

        zip.generateAsync({ type: 'blob' }).then((content) => {
            saveAs(content, 'calibration_scan_logs.zip');
        });
        setLoading(false);
    };

    const handleDownloadZipForCalibrationScanData = async (rows: any[]) => {
        setLoading(true);
        const zip = new JSZip();
        for (const row of rows) {
            const folderName = `${row.scan_id}_${row.sub_scan_id}`;
            const folder = zip.folder(folderName);

            const filesToDownload = [
                { url: row.full_raw_top, name: 'full_raw_top' },
                { url: row.full_raw_bottom, name: 'full_raw_bottom' },
                { url: row.cropped_top, name: 'cropped_top' },
                { url: row.cropped_bottom, name: 'cropped_bottom' },
                { url: row.processed_segmented, name: 'processed_segmented' },
                { url: row.processed_classes, name: 'processed_classes' },
            ];

            for (const file of filesToDownload) {
                if (file.url) {
                    try {
                        const response = await fetch(file.url);
                        const blob = await response.blob();
                        const extension = file.url.split('.').pop()?.split(/[#?]/)[0] || 'jpg';
                        folder?.file(`${file.name}.${extension}`, blob);
                    } catch (err) {
                        console.error(`Failed to fetch ${file.url}`, err);
                    }
                }
            }
        }

        zip.generateAsync({ type: 'blob' }).then((content: Blob) => {
            saveAs(content, 'calibration_scan_images.zip');
        });
        setLoading(false);
    };

    const handleDownloadCalibrationDataImages = async (row: CalibrationData) => {
        setLoading(true);
        const { calibration_id, calibration_images } = row;

        if (!calibration_images || calibration_images.length === 0) {
            errorMessage("No images to Download");
            return;
        }

        const zip = new JSZip();
        const folder = zip.folder(`Images_${calibration_id}`);

        for (const image of calibration_images) {
            try {
                const response = await fetch(image.file_url);
                const blob = await response.blob();

                if (folder) {
                    folder.file(image.file_name || "unnamed_file", blob);
                } else {
                    console.error("Failed to create zip folder.");
                }
            } catch (error) {
                console.error(`Failed to fetch image ${image.file_url}`, error);
            }
        }

        zip.generateAsync({ type: "blob" }).then((content) => {
            saveAs(content, `Images_${calibration_id}.zip`);
        });
        setLoading(false);
    };

    const handleDownloadCSV = () => {
        if (!calibrationScanData || calibrationScanData.length === 0) return;

        const headers = Object.keys(calibrationScanData[0]);
        const csvRows = [headers.join(',')];

        for (const row of calibrationScanData) {
            const values = headers.map(key => {
                let val = row[key];
                if (Array.isArray(val)) {
                    val = val.join(',');
                    val = '\u200B' + val;
                }

                if (val === null || val === undefined) return '';
                val = String(val).replace(/"/g, '""');
                return `"${val}"`;
            });

            csvRows.push(values.join(','));
        }

        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, `${selectedScanDataType}_data.csv`);
    };

    const handleRowSelectionChange = (ids: string[]) => {
        const newSelectedRows = calibrationScanData.filter((row: CalibrationScanData) =>
            ids.includes(row.id)
        );
        setSelectedRows(newSelectedRows);
    };

    const handleLogRowSelectionChange = (ids: string[]) => {
        const newSelectedRows = combinedCalibrationData?.calibration_scan_logs?.filter((row: CalibrationScanLogs) =>
            ids.includes(row.id)
        );
        setSelectedLogRows(newSelectedRows ?? [])
    };

    return (
        <>
            {loading ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box>
                    <main className="m-a-20">
                        <Formik
                            initialValues={{
                                companyId: [],
                                cropId: [],
                            }}
                            onSubmit={handleSubmit}
                        >
                            {({ values, handleSubmit, setFieldValue }) => (
                                <Form onSubmit={handleSubmit}>
                                    <div className="d-flex justify-end">
                                        <div className="d-flex justify-between w-100">
                                            <Grid container spacing={2}>
                                                <Grid size={2}>
                                                    <Dropdown
                                                        options={companies.map((company) => ({
                                                            id: company.company_id,
                                                            name: company.company_name,
                                                        }))}
                                                        value={values.companyId}
                                                        onChange={(id) => setFieldValue("companyId", id)}
                                                        label="Company"
                                                        checkmark
                                                    />
                                                </Grid>
                                                <Grid size={2}>
                                                    <Dropdown
                                                        options={crops.map((crop) => ({
                                                            id: crop.crop_id,
                                                            name: crop.crop_name,
                                                        }))}
                                                        value={values.cropId}
                                                        onChange={(id) => setFieldValue("cropId", id)}
                                                        label="Crop"
                                                        checkmark
                                                    />
                                                </Grid>
                                                <Grid size={4}>
                                                    <DateRange value={dateRange} onChange={setDateRange} />
                                                </Grid>
                                                <Grid size={2}>
                                                    <Button
                                                        label="View"
                                                        type="submit"
                                                        variant="contained"
                                                        color="primary"
                                                        className="m-t-10"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </div>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                        <Box sx={{ mt: 3 }}>
                            <Grid container>
                                <Grid size={2}>
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                checked={selectedSection === "calibration_scan_data"}
                                                onChange={handleRadioChange}
                                                value="calibration_scan_data"
                                                name="calibration_option"
                                            />
                                        }
                                        label="Calibration Scan Data"
                                    />
                                </Grid>
                                <Grid size={2}>
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                checked={selectedSection === "calibration_scan_logs"}
                                                onChange={handleRadioChange}
                                                value="calibration_scan_logs"
                                                name="calibration_option"
                                            />
                                        }
                                        label="Calibration Scan Logs"
                                    />
                                </Grid>
                                <Grid size={2}>
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                checked={selectedSection === "calibration_data"}
                                                onChange={handleRadioChange}
                                                value="calibration_data"
                                                name="calibration_option"
                                            />
                                        }
                                        label="Calibration Data"
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                        {selectedSection === "calibration_scan_data" && (
                            <Box sx={{ m: 3 }}>
                                <Grid container spacing={2}>
                                    {calibrationScanDataOptions.map((option) => (
                                        <Grid key={option.value}>
                                            <FormControlLabel
                                                control={
                                                    <Radio
                                                        checked={selectedScanDataType === option.value}
                                                        onChange={() => setSelectedScanDataType(option.value)}
                                                        value={option.value}
                                                        name="scan_data_type"
                                                    />
                                                }
                                                label={option.label}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                                <div className="m-t-30 d-flex justify-end">
                                    <div className="d-flex justify-between w-100">
                                        <TextField
                                            className="w-25"
                                            label="Search"
                                            variant="outlined"
                                            value={searchQuery}
                                            size="small"
                                            startIcon={<SearchIcon />}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        {selectedScanDataType === 'images' && selectedRows.length > 0 && (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                label="Download Images"
                                                onClick={() => handleDownloadZipForCalibrationScanData(selectedRows)}
                                            />
                                        )}
                                        {selectedScanDataType !== 'images' && (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                label="Download CSV"
                                                onClick={handleDownloadCSV}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="ag-theme-alpine m-t-20" style={{ height: '100%', width: '100%' }}>
                                    {combinedCalibrationData &&
                                        ['grain', 'mapping', 'points', 'performance', 'images'].includes(selectedScanDataType) && (
                                            <GridTable
                                                key={selectedScanDataType}
                                                rows={calibrationScanData}
                                                columns={calibrationScanColumns[selectedScanDataType]}
                                                pageSize={10}
                                                searchQuery={searchQuery}
                                                setSearchQuery={setSearchQuery}
                                                getRowId={(row) => `${row.scan_id}_${row.sub_scan_id}_${selectedScanDataType}`}
                                                checkboxSelection={selectedScanDataType === 'images'}
                                                onSelectionModelChange={(selectedIds) => {
                                                    handleRowSelectionChange(selectedIds as string[]);
                                                }}
                                            />
                                        )}
                                </div>
                            </Box>
                        )}
                        {selectedSection === "calibration_scan_logs" && (
                            <Box sx={{ m: 3 }}>
                                <div className="m-t-30 d-flex justify-end">
                                    <div className="d-flex justify-between w-100">
                                        <TextField
                                            className="w-25"
                                            label="Search"
                                            variant="outlined"
                                            value={searchQuery}
                                            size="small"
                                            startIcon={<SearchIcon />}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        {selectedLogRows.length > 0 && (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                label="Download"
                                                onClick={() => handleDownloadZipForCalibrationScanLogs(selectedLogRows)}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="ag-theme-alpine m-t-20" style={{ height: '100%', width: '100%' }}>
                                    {combinedCalibrationData &&
                                        <GridTable
                                            rows={combinedCalibrationData.calibration_scan_logs}
                                            columns={calinbrationScanLogColumns}
                                            pageSize={10}
                                            searchQuery={searchQuery}
                                            setSearchQuery={setSearchQuery}
                                            getRowId={(row) => row.id}
                                            checkboxSelection
                                            onSelectionModelChange={(selectedIds) => {
                                                handleLogRowSelectionChange(selectedIds as string[]);
                                            }}
                                        />
                                    }
                                </div>
                            </Box>
                        )}
                        {selectedSection === "calibration_data" && (
                            <Box sx={{ m: 3 }}>
                                <div className="m-t-30 d-flex justify-end">
                                    <div className="d-flex justify-between w-100">
                                        <TextField
                                            className="w-25"
                                            label="Search"
                                            variant="outlined"
                                            value={searchQuery}
                                            size="small"
                                            startIcon={<SearchIcon />}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="ag-theme-alpine m-t-20" style={{ height: '100%', width: '100%' }}>
                                    {combinedCalibrationData &&
                                        <GridTable
                                            rows={combinedCalibrationData.calibration_data}
                                            columns={calinbrationDataColumns}
                                            pageSize={10}
                                            searchQuery={searchQuery}
                                            setSearchQuery={setSearchQuery}
                                            getRowId={(row) => row.id}
                                        />
                                    }
                                </div>
                            </Box>
                        )}
                    </main>
                </Box>
            )}
        </>
    );
};

export default Calibration;
